import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
    CContainer, CRow, CCol, CCard, CCardBody, CCardHeader, CButton, CSpinner,
    CButtonGroup, CFormCheck, CModal, CModalHeader, CModalTitle, CModalBody,
    CModalFooter, CFormInput, CAlert, CBadge
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilBell, cilAccountLogout, cilUser, cilVideo, cilClone, cilTrash, cilLink, cilHome, cilFolder, cilSettings } from '@coreui/icons';
import { toast } from 'react-toastify';
import Loading from '../../components/Loading/Loading';
import MultiCameraAnalytics from '../../components/MultiCameraAnalytics/MultiCameraAnalytics';
import './DashboardPage.css';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../components/LanguageSwitcher/LanguageSwitcher';

// ПРАВА
import { getUserRoles } from '../../utils/authUtils';

// НОВЫЕ АУДИО-КОМПОНЕНТЫ
import AudioRulesModal from '../../components/AudioRulesModal/AudioRulesModal';

const DashboardPage = () => {
    const { t } = useTranslation();
    const [cameras, setCameras] = useState([]);
    const [stores, setStores] = useState([]);
    const [unreadAlerts, setUnreadAlerts] = useState([]); // Стейт для непрочитанных алармов
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [selectedCameraIds, setSelectedCameraIds] = useState([]);
    const [showHint, setShowHint] = useState(false);

    // Стейт для модалки аудио-правил
    const [rulesModalVisible, setRulesModalVisible] = useState(false);

    // Права пользователя
    const { is_staff, is_superuser } = getUserRoles();
    const hasAdminRights = is_staff || is_superuser;

    // Модалка для объединения 2+ камер в ОДИН СОВМЕЩЕННЫЙ ПОСТ (Camera)
    const [combineModal, setCombineModal] = useState({ visible: false, selectedCams: [], name: '' });

    // Модалка для группировки камер в МАГАЗИН (Store)
    const [createStoreModal, setCreateStoreModal] = useState({ visible: false, name: '' });

    const fetchInitialData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const [camRes, userRes, storeRes, alertsRes] = await Promise.all([
                axios.get('/api/cameras/', { headers: { 'Authorization': `Bearer ${token}` } }),
                axios.get('/api/profile/', { headers: { 'Authorization': `Bearer ${token}` } }),
                axios.get('/api/stores/', { headers: { 'Authorization': `Bearer ${token}` } }),
                axios.get('/api/audio-alerts/?is_reviewed=false', { headers: { 'Authorization': `Bearer ${token}` } }) // Загружаем алармы
            ]);
            setCameras(camRes.data.results || []);
            setUser(userRes.data);
            setStores(storeRes.data.results || []);
            setUnreadAlerts(alertsRes.data.results || []);
        } catch (err) {
            toast.error(t('common.error'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInitialData();
    }, [t]);

    useEffect(() => {
        if (cameras.filter(c => !c.parent && !c.is_combined_camera).length > 1 && selectedCameraIds.length === 0) {
            const timer = setTimeout(() => setShowHint(true), 1500);
            return () => clearTimeout(timer);
        } else {
            setShowHint(false);
        }
    }, [cameras, selectedCameraIds]);

    const handleCameraSelect = (cameraId) => {
        setSelectedCameraIds(prev =>
            prev.includes(cameraId)
                ? prev.filter(id => id !== cameraId)
                : [...prev, cameraId]
        );
    };

    // ФУНКЦИОНАЛ СТАРЫЙ: ДУБЛИРОВАНИЕ
    const handleDuplicate = async (cameraId) => {
        if (!window.confirm(t('dashboard.confirm_duplicate'))) return;
        try {
            const token = localStorage.getItem('token');
            await axios.post(`/api/cameras/${cameraId}/duplicate/`, {}, { headers: { 'Authorization': `Bearer ${token}` } });
            toast.success(t('common.success'));
            fetchInitialData();
        } catch (err) {
            toast.error(err.response?.data?.error || t('common.error'));
        }
    };

    // ФУНКЦИОНАЛ СТАРЫЙ: УДАЛЕНИЕ (Дубли и Совмещенные)
    const handleDelete = async (postId, postName) => {
        if (!window.confirm(`${t('dashboard.btn_delete')} "${postName}"?`)) return;
        setDeletingId(postId);
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`/api/cameras/${postId}/`, { headers: { 'Authorization': `Bearer ${token}` } });
            toast.success(t('common.success'));
            setSelectedCameraIds(prev => prev.filter(id => id !== postId));
            await fetchInitialData();
        } catch (error) {
            toast.error(error.response?.data?.error || t('common.error'));
        } finally {
            setDeletingId(null);
        }
    };

    // ФУНКЦИОНАЛ СТАРЫЙ: ОБЪЕДИНЕНИЕ В СОВМЕЩЕННЫЙ ПОСТ (Camera)
    const openCombineModal = () => {
        const selectedMasterCams = cameras.filter(c => selectedCameraIds.includes(c.id) && !c.parent && !c.is_combined_camera);
        if (selectedMasterCams.length < 2) return;

        let generatedName = "Совмещенный: " + selectedMasterCams.slice(0, 3).map(c => c.name).join(' + ');
        if (selectedMasterCams.length > 3) {
            generatedName += ` и еще ${selectedMasterCams.length - 3}...`;
        }

        setCombineModal({
            visible: true,
            selectedCams: selectedMasterCams,
            name: generatedName
        });
    };

    const closeCombineModal = () => setCombineModal({ visible: false, selectedCams: [], name: '' });

    const handleCombine = async () => {
        if (!combineModal.name.trim()) {
            toast.warn('Название не может быть пустым');
            return;
        }
        try {
            const token = localStorage.getItem('token');
            const payload = {
                camera_ids: combineModal.selectedCams.map(c => c.id),
                name: combineModal.name
            };
            await axios.post('/api/cameras/combine/', payload, { headers: { 'Authorization': `Bearer ${token}` } });
            toast.success('Совмещенный пост успешно создан!');
            closeCombineModal();
            setSelectedCameraIds([]);
            fetchInitialData();
        } catch (err) {
            toast.error(err.response?.data?.error || t('common.error'));
        }
    };

    // ФУНКЦИОНАЛ НОВЫЙ: СОЗДАНИЕ И УДАЛЕНИЕ МАГАЗИНА (Store)
    const handleCreateStore = async () => {
        if (!createStoreModal.name.trim()) {
            toast.warn('Введите название магазина');
            return;
        }
        try {
            const token = localStorage.getItem('token');
            await axios.post('/api/stores/', {
                name: createStoreModal.name,
                camera_ids: selectedCameraIds
            }, { headers: { 'Authorization': `Bearer ${token}` } });

            toast.success('Группа (Магазин) успешно создана!');
            setCreateStoreModal({ visible: false, name: '' });
            setSelectedCameraIds([]);
            fetchInitialData();
        } catch (e) {
            toast.error('Ошибка при создании магазина');
        }
    };

    const handleDeleteStore = async (storeId, storeName) => {
        if (!window.confirm(`Расформировать группу "${storeName}"? Сами камеры удалены НЕ будут.`)) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`/api/stores/${storeId}/`, { headers: { 'Authorization': `Bearer ${token}` } });
            toast.success('Группа удалена');
            fetchInitialData();
        } catch (e) {
            toast.error('Ошибка при удалении группы');
        }
    };

    const handleLogout = () => { localStorage.removeItem('token'); window.location.href = '/login'; };

    if (loading) return <Loading name={t('common.loading')} />;

    const selectedCamerasData = cameras.filter(cam => selectedCameraIds.includes(cam.id));
    const masterCamerasSelected = selectedCamerasData.filter(c => !c.parent && !c.is_combined_camera);

    // Вычисляем, какие камеры уже в магазинах, а какие одиночные
    const assignedCameraIds = new Set(stores.flatMap(s => s.cameras.map(c => c.id)));
    const standaloneCameras = cameras.filter(c => !assignedCameraIds.has(c.id));

    // УНИВЕРСАЛЬНЫЙ РЕНДЕР КАРТОЧКИ ПОСТА
    const renderCameraCard = (cam, index = 0, isStandalone = true) => (
        <CCol md={6} key={cam.id} className="mb-3 d-flex align-items-stretch">
            <div className="hint-wrapper w-100">
                {index === 0 && isStandalone && showHint && (
                    <div className="animated-hint">
                        {t('dashboard.hint_select')}
                        <svg width="50px" height="50px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 18L6 6" stroke="#321fdb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /><path d="M8 18L18 18L18 8" stroke="#321fdb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </div>
                )}
                <CCard className="h-100 shadow-sm d-flex flex-column w-100">
                    <CCardHeader className="d-flex justify-content-between align-items-center">
                        <strong><CIcon icon={cilVideo} className="me-2" />{cam.name}</strong>
                        <CFormCheck id={`cam-check-${cam.id}`} checked={selectedCameraIds.includes(cam.id)} onChange={() => handleCameraSelect(cam.id)} />
                    </CCardHeader>
                    <CCardBody className="d-flex flex-column">
                        <div className="mb-2 text-muted small" style={{ wordBreak: 'break-all' }}>
                            {cam.is_duplicate && `(Дубль поста #${cam.parent})`}
                            {cam.is_combined && `(Совмещенный: #${cam.parent} + ${cam.additional_cameras?.length || 0} доп. камер)`}
                            {/* ПОЛНАЯ ССЫЛКА БЕЗ УРЕЗАНИЯ */}
                            {!cam.parent && !cam.is_combined_camera && cam.rtsp_url}
                        </div>

                        {/* Кнопки прижаты к низу благодаря mt-auto */}
                        <CButtonGroup className="mt-auto w-100">
                            <Link to={`/camera/${cam.id}`} className="text-decoration-none w-100">
                                <CButton color="primary" variant="outline" className="w-100 rounded-end-0">{t('dashboard.btn_analytics')}</CButton>
                            </Link>

                            {hasAdminRights && !cam.parent && !cam.is_combined_camera && (
                                <CButton color="secondary" variant="outline" onClick={() => handleDuplicate(cam.id)} title={t('dashboard.btn_duplicate')}><CIcon icon={cilClone} /></CButton>
                            )}

                            {hasAdminRights && (cam.parent || cam.is_combined_camera) && (
                                <CButton color="danger" variant="outline" onClick={() => handleDelete(cam.id, cam.name)} title={t('dashboard.btn_delete')} disabled={deletingId === cam.id} className="rounded-start-0">{deletingId === cam.id ? <CSpinner size="sm" /> : <CIcon icon={cilTrash} />}</CButton>
                            )}
                        </CButtonGroup>
                    </CCardBody>
                </CCard>
            </div>
        </CCol>
    );

    return (
        <div className="bg-light min-vh-100 d-flex flex-row">
            <CContainer>
                <CRow className="justify-content-center">
                    <CCol md={10}>
                        <div className="d-flex justify-content-between align-items-center mb-4 mt-4">
                            <div>
                                <h2>{t('dashboard.title')}</h2>
                                {user && <p className="text-medium-emphasis mb-0">{t('dashboard.org')}: {user.organization?.name || '---'}</p>}
                            </div>
                            <div className="d-flex align-items-center">

                                {/* КНОПКА: Настройка аудио-скриптов и Алармов */}
                                {hasAdminRights && (
                                    <CButton color="primary" variant="outline" onClick={() => setRulesModalVisible(true)} className="me-2 fw-bold">
                                        <CIcon icon={cilSettings} className="me-2" /> Скрипты и Алармы
                                    </CButton>
                                )}

                                {/* КНОПКА: Создать Магазин */}
                                {hasAdminRights && selectedCameraIds.length > 0 && (
                                    <CButton color="success" className="text-white me-2" onClick={() => setCreateStoreModal({ visible: true, name: `Группа (${selectedCameraIds.length} постов)` })}>
                                        <CIcon icon={cilHome} className="me-2" /> В группу ({selectedCameraIds.length})
                                    </CButton>
                                )}

                                {/* КНОПКА: Совместить посты (Camera) */}
                                {hasAdminRights && masterCamerasSelected.length >= 2 && (
                                    <CButton color="info" className="text-white me-2" onClick={openCombineModal}>
                                        <CIcon icon={cilLink} className="me-2" /> {t('dashboard.combine_btn')} ({masterCamerasSelected.length})
                                    </CButton>
                                )}

                                {/* Кнопка: Уведомления и чаты Битрикс / Телеграм */}
                                <CButton color="warning" variant="outline" as={Link} to="/notifications" className="me-2 fw-bold">
                                    <CIcon icon={cilBell} className="me-2" /> Уведомления и Чаты
                                </CButton>

                                {user?.is_staff && (
                                    <CButton color="dark" variant="outline" as={Link} to="/users" className="me-2">
                                        <CIcon icon={cilUser} className="me-2" /> {t('dashboard.manage_users')}
                                    </CButton>
                                )}
                                <CButton color="secondary" onClick={handleLogout}><CIcon icon={cilAccountLogout} className="me-2" />{t('dashboard.logout')}</CButton>
                                <LanguageSwitcher />
                            </div>
                        </div>

                        {cameras.length === 0 ? (
                            <CCard><CCardBody>{t('dashboard.no_posts')}</CCardBody></CCard>
                        ) : (
                            <>
                                {/* БЛОК 1: ОТОБРАЖЕНИЕ МАГАЗИНОВ (ГРУПП) С БЕЙДЖАМИ АЛАРМОВ */}
                                {stores.map(store => (
                                    <div key={store.id} className="store-group-wrapper">
                                        <div className="store-group-header">
                                            <div className="d-flex align-items-center gap-3">
                                                <h4 className="m-0 text-primary d-flex align-items-center">
                                                    <CIcon icon={cilHome} size="xl" className="me-2" /> {store.name}
                                                </h4>

                                                {/* НОВЫЙ БЛОК ДЛЯ БЕЙДЖА АЛАРМОВ */}
                                                {(() => {
                                                    const storeAlerts = unreadAlerts.filter(a => store.cameras.some(c => c.id === a.camera));
                                                    if (storeAlerts.length > 0) {
                                                        const hasRed = storeAlerts.some(a => a.severity === 'red_alert');
                                                        return (
                                                            <CBadge color={hasRed ? 'danger' : 'warning'} className="p-2 fs-6 shadow-sm">
                                                                {hasRed ? '🚨' : '⚠️'} {storeAlerts.length} непрочитанных уведомлений
                                                            </CBadge>
                                                        );
                                                    }
                                                    return null;
                                                })()}
                                            </div>
                                            <div className="d-flex align-items-center gap-2">
                                                <Link to={`/store/${store.id}`}>
                                                    <CButton color="primary" className="fw-bold px-4">
                                                        Смотреть всё (Аналитика и Эфир)
                                                    </CButton>
                                                </Link>
                                                {hasAdminRights && (
                                                    <CButton color="danger" variant="outline" onClick={() => handleDeleteStore(store.id, store.name)}>
                                                        <CIcon icon={cilTrash} />
                                                    </CButton>
                                                )}
                                            </div>
                                        </div>
                                        <CRow>
                                            {store.cameras.map((cam, idx) => renderCameraCard(cam, idx, false))}
                                        </CRow>
                                    </div>
                                ))}

                                {/* БЛОК 2: ОТДЕЛЬНЫЕ ПОСТЫ */}
                                {standaloneCameras.length > 0 && (
                                    <div className="mt-4">
                                        {stores.length > 0 && (
                                            <h5 className="text-secondary mb-3"><CIcon icon={cilFolder} className="me-2" />Отдельные посты</h5>
                                        )}
                                        <CRow>
                                            {standaloneCameras.map((cam, index) => renderCameraCard(cam, index, true))}
                                        </CRow>
                                    </div>
                                )}
                            </>
                        )}
                    </CCol>

                    {/* СВОДНАЯ АНАЛИТИКА ПО ВЫБРАННЫМ ГАЛОЧКАМ */}
                    {selectedCameraIds.length > 0 && (<MultiCameraAnalytics selectedCameras={selectedCamerasData} />)}
                </CRow>

                {/* МОДАЛКА 1: ОБЪЕДИНЕНИЕ КАМЕР В СОВМЕЩЕННЫЙ ПОСТ (Camera) */}
                <CModal alignment="center" visible={combineModal.visible} onClose={closeCombineModal}>
                    <CModalHeader><CModalTitle>{t('dashboard.combine_modal_title')}</CModalTitle></CModalHeader>
                    <CModalBody>
                        <p>{t('dashboard.combine_modal_body_1')} {combineModal.selectedCams.length} камер:</p>
                        <ul>
                            {combineModal.selectedCams.map(cam => (
                                <li key={cam.id}><strong>{cam.name}</strong></li>
                            ))}
                        </ul>
                        <CAlert color="info">{t('dashboard.combine_modal_alert')}</CAlert>
                        <CFormInput
                            label={t('dashboard.combine_name_label')}
                            value={combineModal.name}
                            onChange={(e) => setCombineModal(prev => ({ ...prev, name: e.target.value }))}
                        />
                    </CModalBody>
                    <CModalFooter>
                        <CButton color="secondary" onClick={closeCombineModal}>{t('common.cancel')}</CButton>
                        <CButton color="primary" onClick={handleCombine}>{t('dashboard.combine_create_btn')}</CButton>
                    </CModalFooter>
                </CModal>

                {/* МОДАЛКА 2: СОЗДАНИЕ ГРУППЫ / МАГАЗИНА (Store) */}
                <CModal alignment="center" visible={createStoreModal.visible} onClose={() => setCreateStoreModal({ visible: false, name: '' })}>
                    <CModalHeader><CModalTitle>Создание группы (Магазина)</CModalTitle></CModalHeader>
                    <CModalBody>
                        <p>Выбрано постов для объединения в группу: <strong>{selectedCameraIds.length} шт.</strong></p>
                        <CFormInput
                            label="Название группы / филиала:"
                            placeholder="Например: Магазин на Невском"
                            value={createStoreModal.name}
                            onChange={(e) => setCreateStoreModal({ ...createStoreModal, name: e.target.value })}
                        />
                    </CModalBody>
                    <CModalFooter>
                        <CButton color="secondary" onClick={() => setCreateStoreModal({ visible: false, name: '' })}>{t('common.cancel')}</CButton>
                        <CButton color="success" className="text-white" onClick={handleCreateStore}>Сохранить группу</CButton>
                    </CModalFooter>
                </CModal>

                {/* МОДАЛКА 3: НАСТРОЙКИ АУДИО-ПРАВИЛ */}
                <AudioRulesModal
                    visible={rulesModalVisible}
                    onClose={() => setRulesModalVisible(false)}
                />

            </CContainer>
        </div>
    );
};
export default DashboardPage;